import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, 
  Typography, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Dialog, 
  DialogTitle,
  DialogContent, 
  DialogActions, 
  TextField, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Switch, 
  IconButton, 
  Tooltip,
  Chip, 
  FormControlLabel, 
  CircularProgress, 
  Snackbar, 
  Alert, 
  Divider, 
  useTheme,
  useMediaQuery,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Collapse,
  Fade,
  ListItemIcon,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Palette as PaletteIcon,
  Sort as SortIcon,
  CheckCircle as CheckCircleIcon,
  ToggleOff as ToggleOffIcon,
  Label as LabelIcon,
  FormatColorFill as ColorIcon
} from '@mui/icons-material';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { statusAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

// Modern color palette
const COLORS = {
  primary: "#4361ee",
  secondary: "#3f37c9",
  success: "#4cc9f0",
  warning: "#f72585",
  info: "#4895ef",
  light: "#f8f9fa",
  dark: "#212529",
  gray: "#6c757d",
  cardBg: "#ffffff",
  surface: "#f0f4f8"
};

const StatusPage = () => {
  const { user: authUser } = useAuth();
  const canCreate = authUser?.hasPermission('status.create');
  const canUpdate = authUser?.hasPermission('status.update');
  const canDelete = authUser?.hasPermission('status.delete');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [statuses, setStatuses] = useState([]);
  const [type, setType] = useState('project');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    key: '',
    color: '#1976d2',
    type: 'project',
    order: 1,
    is_active: true,
  });
  const [editingId, setEditingId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [expandedCardId, setExpandedCardId] = useState(null);

  const fetchStatuses = useCallback(async () => {
    setLoading(true);
    try {
      const res = await statusAPI.getAll(type);
      const sorted = res.data.sort((a, b) => a.order - b.order);
      setStatuses(sorted);
    } catch (err) {
      console.error('Error fetching statuses:', err);
      setSnackbar({ open: true, message: 'Failed to load statuses', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [type]);

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'order' ? Math.max(parseInt(value) || 1, 1) : value,
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      key: '',
      color: '#1976d2',
      type,
      order: statuses.length ? Math.max(...statuses.map((s) => s.order)) + 1 : 1,
      is_active: true,
    });
    setEditingId(null);
  };

  const handleOpenAdd = () => {
    resetForm();
    setOpenDialog(true);
  };

  const handleEdit = (status) => {
    setFormData({ ...status });
    setEditingId(status.id);
    setOpenDialog(true);
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      if (editingId) {
        await statusAPI.update(editingId, formData);
        setSnackbar({ open: true, message: 'Status updated successfully', severity: 'success' });
      } else {
        await statusAPI.create(formData);
        setSnackbar({ open: true, message: 'Status created successfully', severity: 'success' });
      }

      await fetchStatuses();
      setOpenDialog(false);
    } catch (err) {
      let message = 'Failed to save status. Please try again.';
      if (err.response?.data?.errors) {
        const errors = err.response.data.errors;
        const firstField = Object.keys(errors)[0];
        message = errors[firstField][0];
      } else if (err.response?.data?.error) {
        message = err.response.data.error;
      }
      console.error('Error saving status:', err);
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this status?')) return;
    setSaving(true);
    try {
      await statusAPI.delete(id);
      setStatuses((prev) => prev.filter((s) => s.id !== id));
      setSnackbar({ open: true, message: 'Status deleted', severity: 'success' });
    } catch (err) {
      console.error('Error deleting status:', err);
      setSnackbar({ open: true, message: 'Failed to delete status', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      const updated = statuses.map((s) =>
        s.id === id ? { ...s, is_active: !s.is_active } : s
      );
      setStatuses(updated);
      await statusAPI.toggle(id);
      setSnackbar({ open: true, message: 'Status toggled', severity: 'success' });
    } catch (err) {
      console.error('Error toggling status:', err);
      setSnackbar({ open: true, message: 'Failed to toggle status', severity: 'error' });
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    const reordered = Array.from(statuses);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    const updated = reordered.map((s, idx) => ({ ...s, order: idx + 1 }));
    setStatuses(updated);
    try {
      await statusAPI.reorder({
        order: updated.map(({ id, order }) => ({ id, order })),
      });
      setSnackbar({ open: true, message: 'Order updated', severity: 'success' });
    } catch (err) {
      console.error('Error reordering:', err);
      setSnackbar({ open: true, message: 'Failed to reorder', severity: 'error' });
    }
  };

  const renderActiveToggle = (status) => (
    <Box display="flex" alignItems="center" gap={1}>
      <Switch
        size="small"
        checked={status.is_active}
        onChange={() => handleToggle(status.id)}
        color={status.is_active ? 'success' : 'error'}
      />
      <Typography variant="body2" color={status.is_active ? 'success.main' : 'error.main'}>
        {status.is_active ? 'Active' : 'Inactive'}
      </Typography>
    </Box>
  );

  // Mobile Card Component
  const StatusCard = ({ status, index }) => {
    const isExpanded = expandedCardId === status.id;
    
    return (
      <Fade in timeout={300}>
        <Card 
          variant="outlined" 
          sx={{ 
            mb: 1.5, 
            borderRadius: 2,
            overflow: 'hidden',
            transition: 'box-shadow 0.2s',
            '&:hover': { boxShadow: 2 }
          }}
        >
          <CardHeader
            title={
              <Box display="flex" alignItems="center" gap={1}>
                <DragIcon fontSize="small" />
                <Typography variant="h6" fontWeight="600">{status.name}</Typography>
                <Chip label={status.key} size="small" variant="outlined" />
              </Box>
            }
            action={
              <IconButton onClick={() => setExpandedCardId(isExpanded ? null : status.id)}>
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            }
            sx={{ pb: 0 }}
          />
          <CardContent sx={{ pt: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  bgcolor: status.color,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              />
              <Typography variant="body2">Order: {status.order}</Typography>
              {renderActiveToggle(status)}
            </Stack>
            
            <Collapse in={isExpanded}>
              <Box mt={1} pt={1} borderTop="1px solid" borderColor="divider">
                <Typography variant="body2" color="text.secondary">
                  ID: {status.id}
                </Typography>
                <Box display="flex" justifyContent="flex-end" mt={1}>
                  <Tooltip title="Edit">
                    {canUpdate && (
                      <IconButton onClick={() => handleEdit(status)}>
                        <EditIcon />
                      </IconButton>
                    )}
                  </Tooltip>
                  <Tooltip title="Delete">
                    {canDelete && (
                      <IconButton onClick={() => handleDelete(status.id)}>
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Tooltip>
                </Box>
              </Box>
            </Collapse>
          </CardContent>
        </Card>
      </Fade>
    );
  };

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
        {/* Header Section */}
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' }, 
            justifyContent: 'space-between', 
            alignItems: 'flex-start', 
            mb: 3,
            gap: 2
          }} 
        >
          <Box>
            <Typography variant="h4" fontWeight="600" color="text.primary">
              Status Management
            </Typography>
            {/* <Typography variant="body2" color="text.secondary">
              Manage workflow statuses for {type}s
            </Typography> */}
          </Box>
          
          <Box 
            sx={{ 
              display: 'flex', 
              gap: 1.5, 
              flexWrap: 'wrap', 
              alignItems: 'center',
              width: { xs: '100%', sm: 'auto' },
              justifyContent: { xs: 'flex-end', sm: 'initial' }
            }}
          >
            <FormControl 
              size="small" 
              sx={{ 
                minWidth: { xs: '100%', sm: 120 },
                maxWidth: { xs: '100%', sm: 150 }
              }}
            >
              <InputLabel>Type</InputLabel>
              <Select
                value={type}
                label="Type"
                onChange={(e) => setType(e.target.value)}
                sx={{ bgcolor: 'background.paper', borderRadius: "10px" }}
              >
                <MenuItem value="project">Project</MenuItem>
                <MenuItem value="task">Task</MenuItem>
              </Select>
            </FormControl>
            
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenAdd}
              sx={{
                height: 48, // Slightly taller for better touch target
                px: 3,
                borderRadius: 3, // More rounded corners (pill-like)
                fontWeight: 600,
                letterSpacing: '0.5px',
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                boxShadow: '0 4px 15px rgba(0,0,0,0.15)', 
                '&:hover': {
                  boxShadow: '0 8px 25px rgba(0,0,0,0.25)', // Lift effect
                  transform: 'translateY(-1px)',
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
                width: isMobile ? "100%" : "auto",
              }}
              disabled={saving || !canCreate}
            >
              Add Status
            </Button>
          </Box>
        </Box>

        {/* Content Section */}
        <Paper 
          elevation={0} 
          variant="outlined" 
          sx={{ 
            borderRadius: 2, 
            overflow: 'hidden',
            p: { xs: 0, md: 1 }
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : isMobile ? (
            // Mobile View: Cards
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="statuses">
                {(provided) => (
                  <List 
                    ref={provided.innerRef} 
                    {...provided.droppableProps}
                    sx={{ py: 0 }}
                  >
                    {statuses.map((status, index) => (
                      <Draggable key={status.id} draggableId={String(status.id)} index={index} >
                        {(provided, snapshot) => (
                          <ListItem className="mobileStatusManagementList"
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            sx={{ 
                              p: 0, 
                              mb: 1.5,
                              backgroundColor: snapshot.isDragging ? 'action.hover' : 'transparent'
                            }}
                          >
                            <StatusCard status={status} index={index}/>
                          </ListItem>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    {statuses.length === 0 && (
                      <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography color="text.secondary">No statuses found</Typography>
                      </Box>
                    )}
                  </List>
                )}
              </Droppable>
            </DragDropContext>
          ) : (
            // Desktop View: Table
            <DragDropContext onDragEnd={handleDragEnd}>
              <TableContainer>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.50' }}>
                      <TableCell sx={{ width: 40 }} />
                      <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Key</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Color</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Order</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Active</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <Droppable droppableId="statuses">
                    {(provided) => (
                      <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                        {statuses.map((status, index) => (
                          <Draggable key={status.id} draggableId={String(status.id)} index={index}>
                            {(provided, snapshot) => (
                              <TableRow
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                sx={{
                                  backgroundColor: snapshot.isDragging
                                    ? 'action.hover'
                                    : 'background.paper',
                                  '&:hover': { backgroundColor: 'action.hover' },
                                }}
                              >
                                <TableCell {...provided.dragHandleProps}>
                                  <DragIcon fontSize="small" />
                                </TableCell>
                                <TableCell>
                                  <Typography fontWeight={500}>{status.name}</Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip label={status.key} size="small" />
                                </TableCell>
                                <TableCell>
                                  <Box
                                    sx={{
                                      width: 24,
                                      height: 24,
                                      borderRadius: '50%',
                                      bgcolor: status.color,
                                      border: `1px solid ${theme.palette.divider}`,
                                    }}
                                  />
                                </TableCell>
                                <TableCell>{status.order}</TableCell>
                                <TableCell>{renderActiveToggle(status)}</TableCell>
                                <TableCell align="right">
                                  <Tooltip title="Edit">
                                    {canUpdate && (
                                      <IconButton onClick={() => handleEdit(status)}>
                                        <EditIcon />
                                      </IconButton>
                                    )}
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    {canDelete && (
                                      <IconButton onClick={() => handleDelete(status.id)}>
                                        <DeleteIcon />
                                      </IconButton>
                                    )}
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                        {statuses.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                              <Typography color="text.secondary">No statuses found</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    )}
                  </Droppable>
                </Table>
              </TableContainer>
            </DragDropContext>
          )}
        </Paper>
      </Box>

      {/* Dialog */}
      <Dialog 
  open={openDialog} 
  onClose={() => !saving && setOpenDialog(false)} 
  maxWidth="sm" 
  fullWidth
  PaperProps={{
    sx: {
      borderRadius: 2,
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
      border: `1px solid ${theme.palette.divider}`,
    }
  }}
>
  <DialogTitle>
    <Box display="flex" alignItems="center" gap={1}>
      <LabelIcon sx={{ color: theme.palette.primary.main }} />
      {editingId ? 'Edit Status' : 'Add New Status'}
    </Box>
  </DialogTitle>
  <Divider />
  <DialogContent>
    <Stack spacing={2}>
      <TextField
        fullWidth
        label="Status Name"
        name="name"
        value={formData.name}
        onChange={(e) => {
          const value = e.target.value;
          setFormData((prev) => ({
            ...prev,
            name: value,
            key: value
              .toLowerCase()
              .trim()
              .replace(/[\s_]+/g, '_')
              .replace(/[^\w_]+/g, '')
              .replace(/_{2,}/g, '_'),
          }));
        }}
        variant="outlined"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LabelIcon color="action" />
            </InputAdornment>
          ),
        }}
      />
      <TextField
        fullWidth
        label="Key (auto-generated)"
        name="key"
        value={formData.key}
        helperText="Used internally"
        variant="filled"
        InputProps={{
          readOnly: true,
          startAdornment: (
            <InputAdornment position="start">
              <SortIcon color="action" />
            </InputAdornment>
          ),
        }}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} width="100%">
        <TextField
          fullWidth
          label="Color"
          name="color"
          type="color"
          value={formData.color}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <ColorIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          fullWidth
          label="Order"
          name="order"
          type="number"
          value={formData.order}
          onChange={handleChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SortIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Stack>
      <FormControlLabel
        control={
          <Switch
            checked={formData.is_active}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, is_active: e.target.checked }))
            }
            icon={<ToggleOffIcon fontSize="small" />}
            checkedIcon={<CheckCircleIcon fontSize="small" />}
            color="primary"
          />
        }
        label="Active"
        labelPlacement="start"
        sx={{ ml: 0 }}
      />
    </Stack>
  </DialogContent>
  <Divider />
  <DialogActions sx={{ p: 2 }}>
    <Button 
      onClick={() => setOpenDialog(false)} 
      disabled={saving}
      sx={{
        textTransform: 'none',
        fontWeight: 500,
        color: theme.palette.text.secondary,
      }}
    >
      Cancel
    </Button>
    <Button
      onClick={handleSubmit}
      variant="contained"
      disableElevation
      disabled={saving}
      startIcon={saving ? null : <CheckCircleIcon />}
              sx={{
                textTransform: 'none',
                bgcolor: COLORS.secondary,
                '&:hover': {
                  bgcolor: COLORS.primary
                }
              }}
    >
      {saving ? (
        <>
          <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
          {editingId ? 'Updating…' : 'Creating…'}
        </>
      ) : editingId ? (
        'Update Status'
      ) : (
        'Create Status'
      )}
    </Button>
  </DialogActions>
</Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default StatusPage;